<template>
  <div class="segmentation-limiter">
    <v-card class="mt-2">
      <v-card-text class="pt-0">
        <v-row>
          <v-col cols="12" class="pb-0"> <mf-toggle v-model="limiter.active" label="Limitar tamanho do segmento" class="ma-0" hide-details /></v-col>
        </v-row>
        <div v-if="limiter.active">
          <v-row>
            <v-col cols="12" class="pb-0">
              <span class="text-subtitle-1">Limite de clientes no segmento</span>
              <v-text-field
                ref="limitInput"
                v-model="displayLimitValue"
                :disabled="disabled"
                outlined
                dense
                :hide-details="!limitError"
                :error-messages="limitError"
                @input="onLimitInput"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" class="pb-0">
              <span class="text-subtitle-1">Estratégia de limitação</span>
              <v-select v-model="limiter.strategy" hide-details outlined dense :items="limitationStrategies" />
            </v-col>
          </v-row>
          <div v-if="hasLimitationStrategyConfiguration">
            <v-row>
              <v-col cols="12" class="pb-0">
                <span class="text-subtitle-1">Nas lojas</span>
                <mf-select-units-dialog
                  hide-details
                  outlined
                  units-label=""
                  dense
                  enable-inactive-units-selection
                  :check-all-units-on-init="!limiter.unitsIds.length"
                  :selected-units.sync="limiter.unitsIds"
                  :selected-units-configuration.sync="limiter.units"
                  :units="allUnits"
                  :tag-list="tagList"
                  :group-list="unitsGroups"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" class="pb-0">
                <span class="text-subtitle-1">Nos produtos</span>
                <modal-graphql-search-content
                  v-if="!disabled"
                  dense
                  :placeholder="loadingProducts ? 'Carregando' : 'Todos'"
                  :selected-items.sync="limiter.products"
                  item-type="produto"
                  hide-details
                  :loading="loadingProducts"
                />
                <modal-view-items
                  v-else
                  buttonText="Ver produtos"
                  table-title="Lista de produtos"
                  search-placeholder="Busque um produto"
                  :headers="productViewOnlydHeaders"
                  :items="limiter.products"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" class="pb-0">
                <span class="text-subtitle-1">No período</span>
                <segmentation-limiter-data-select v-model="limiter.filterDate" :max-filter-days="90" dense />
              </v-col>
            </v-row>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import ModalGraphqlSearchContent from '@/components/ModalGraphqlSearchContent.vue'
import ModalViewItems from '@/components/ModalViewItems.vue'
import SegmentationLimiterDataSelect from '@/modules/clientes/segmentation/views/components/SegmentationLimiterDataSelect.vue'
import gql from 'graphql-tag'
import { required, minValue } from 'vuelidate/lib/validators'

export const QUERY_PRODUCTS = gql`
  query Products($skus: [String]) {
    products(skus: $skus) {
      products {
        _id
        sku
        ean
        eans
        name
      }
    }
  }
`

export default {
  name: 'SegmentationLimiter',
  components: {
    ModalGraphqlSearchContent,
    ModalViewItems,
    SegmentationLimiterDataSelect,
  },
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    loadingProducts: false,
    limiter: {
      active: false,
      limit: null,
      strategy: 'random',
      unitsIds: [],
      units: [],
      products: [],
      filterDate: {
        field: 'date_dynamic',
        values: ['d-30<='],
      },
    },
    displayLimitValue: null,
  }),
  computed: {
    ...mapGetters(['tagList', 'unitsGroups', 'segmentationLimiter']),
    limitError() {
      if (this.$v.limiter.limit.$error) {
        if (!this.$v.limiter.limit.required) return 'Informe um limite para o segmento ou desative a limitação'
        if (!this.$v.limiter.limit.minValue) return 'O limite deve ser maior que 0'
      }

      return null
    },
    productViewOnlydHeaders() {
      return [
        {
          text: 'SKU',
          align: 'start',
          sortable: false,
          value: 'sku',
          width: '10%',
        },
        {
          text: 'EAN',
          align: 'start',
          sortable: false,
          value: 'ean',
          width: '10%',
        },
        {
          text: 'Nome',
          align: 'start',
          sortable: true,
          value: 'name',
          width: '80%',
        },
      ]
    },
    allUnits() {
      return this.$store.getters.units
    },
    limitationStrategies() {
      return [
        { text: 'Aleatória', value: 'random' },
        { text: 'Maior faturamento', value: 'most_revenue' },
        { text: 'Maior número de cupons', value: 'most_coupons' },
        { text: 'Maior ticket médio', value: 'most_average_ticket' },
        { text: 'Menor faturamento', value: 'least_revenue' },
        { text: 'Menor número de cupons', value: 'least_coupons' },
        { text: 'Menor ticket médio', value: 'least_average_ticket' },
      ]
    },
    hasLimitationStrategyConfiguration() {
      return this.limiter.strategy !== 'random'
    },
  },
  watch: {
    limiter: {
      deep: true,
      handler(newVal) {
        this.updateLimiter()
      },
    },
  },
  mounted() {
    this.mountLimiter()
  },
  methods: {
    ...mapActions(['updateSegmentationLimiter']),
    mountLimiter() {
      if (!this.segmentationLimiter) return

      this.limiter.active = this.segmentationLimiter.active
      this.limiter.strategy = this.segmentationLimiter.strategy
      this.limiter.limit = this.segmentationLimiter.limit

      // Set display value with formatting
      if (this.limiter.limit) {
        this.displayLimitValue = this.limiter.limit.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')
      }

      const unitsFilter = this.segmentationLimiter.filters.find((filter) => filter.field === 'unit')
      const productsFilter = this.segmentationLimiter.filters.find((filter) => filter.field === 'product')
      const dateFilter = this.segmentationLimiter.filters.find((filter) => filter.field === 'date_fixed' || filter.field === 'date_dynamic')
      this.limiter.unitsIds = unitsFilter ? unitsFilter.values : []
      if (dateFilter) {
        this.limiter.filterDate = dateFilter
      }

      this.setProducts(productsFilter)
    },
    updateLimiter() {
      const limiter = {
        active: this.limiter.active && this.limiter.limit !== null && this.limiter.limit !== '',
        strategy: this.limiter.strategy,
        limit: Number(this.limiter.limit || 0),
        filters: [],
      }

      if (this.limiter.strategy !== 'random' && this.limiter.products?.length > 0) {
        limiter.filters.push({
          field: 'product',
          values: this.limiter.products.map((product) => product.sku),
        })
      }

      if (this.limiter.strategy !== 'random' && this.limiter.unitsIds?.length > 0) {
        limiter.filters.push({
          field: 'unit',
          values: this.limiter.unitsIds,
        })
      }

      if (this.limiter.strategy !== 'random' && this.limiter.filterDate) {
        limiter.filters.push({
          field: this.limiter.filterDate.field,
          values: this.limiter.filterDate.values,
        })
      }

      this.updateSegmentationLimiter(limiter)
    },
    async setProducts(productsFilter) {
      if (!productsFilter?.values?.length) return

      try {
        this.loadingProducts = true
        const {
          data: {
            products: { products: productsArray },
          },
        } = await this.$apollo.query({
          query: QUERY_PRODUCTS,
          variables: {
            skus: productsFilter.values,
          },
          fetchPolicy: 'no-cache',
        })
        if (productsArray && productsArray.length) {
          this.limiter.products = productsArray
        }
      } catch (error) {
        console.log(error)
        this.$snackbar({ message: 'Erro ao carregar produtos, tente novamente mais tarde.', snackbarColor: 'error' })
      }
      this.loadingProducts = false
    },
    onLimitInput(value) {
      // Remove all non-digit characters
      const numericValue = value.replace(/\D/g, '')
      const number = numericValue ? Number(numericValue) : null

      // Update the actual limit
      this.limiter.limit = number

      // Format the display value with thousand separators
      if (number) {
        this.displayLimitValue = number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')
      } else {
        this.displayLimitValue = ''
      }
    },
  },
  validations() {
    if (!this.limiter.active) return {}

    return {
      limiter: {
        limit: { required, minValue: minValue(1) },
      },
    }
  },
}
</script>

<style lang="scss">
.segmentation-limiter {
  .title-criterio-details {
    font-size: 20px;
    user-select: none;
  }
}
</style>
